import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, X, BookCheck, Check, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const JENIS_COLOR = {
  'Ziyadah':    'bg-emerald-100 text-emerald-700',
  'Murojaah':   'bg-blue-100    text-blue-700',
  'Bin Nadhar': 'bg-purple-100  text-purple-700',
  "Iqro'":      'bg-amber-100   text-amber-700',
  "Tasmi'":     'bg-rose-100    text-rose-700',
};

function formatRelative(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Baru saja';
  if (diff < 3600) return `${Math.floor(diff / 60)} menit lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
  if (diff < 172800) return 'Kemarin';
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const intervalRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data || []);
    } catch {
      // silently fail – not logged in or not santri
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchNotifications]);

  const handleOpen = () => {
    setOpen(true);
    fetchNotifications();
  };

  const handleRead = async (notif) => {
    if (notif.is_read) return;
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n)
    );
    try {
      await axios.patch(`${API}/notifications/${notif.id}/read`, {}, { withCredentials: true });
    } catch {
      // revert on fail
      setNotifications(prev =>
        prev.map(n => n.id === notif.id ? { ...n, is_read: false } : n)
      );
    }
  };

  const handleReadAll = async () => {
    setMarkingAll(true);
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {/* ignore */} finally {
      setMarkingAll(false);
    }
  };

  const Panel = (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 bg-black/40 z-[999]"
            onClick={() => setOpen(false)}
          />

          {/* Panel */}
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-0 top-0 z-[1000] mx-auto max-w-md bg-white rounded-b-3xl shadow-2xl overflow-hidden"
            style={{ maxHeight: '75vh' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <Bell size={18} className="text-emerald-600" />
                <h2 className="font-bold text-gray-900 text-base">Notifikasi</h2>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    data-testid="notif-read-all-btn"
                    onClick={handleReadAll}
                    disabled={markingAll}
                    className="flex items-center gap-1 text-xs text-emerald-600 font-semibold hover:text-emerald-700 disabled:opacity-60"
                  >
                    {markingAll ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                    Tandai semua dibaca
                  </button>
                )}
                <button
                  data-testid="notif-close-btn"
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded-xl hover:bg-gray-100 text-gray-500"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* List */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(75vh - 68px)' }}>
              {notifications.length === 0 ? (
                <div data-testid="notif-empty-state" className="flex flex-col items-center py-14 text-gray-400">
                  <Bell size={38} className="opacity-25 mb-3" />
                  <p className="text-sm font-medium text-gray-500">Belum ada notifikasi</p>
                  <p className="text-xs mt-1">Notifikasi setoran akan muncul di sini</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {notifications.map((n, i) => (
                    <li key={n.id || i}>
                      <button
                        data-testid={`notif-item-${i}`}
                        onClick={() => handleRead(n)}
                        className={`w-full flex items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-gray-50 ${
                          n.is_read ? 'opacity-60' : 'bg-emerald-50/60'
                        }`}
                      >
                        {/* Icon */}
                        <div className={`flex-shrink-0 mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center ${
                          n.is_read ? 'bg-gray-100' : 'bg-emerald-100'
                        }`}>
                          <BookCheck size={17} className={n.is_read ? 'text-gray-400' : 'text-emerald-600'} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${JENIS_COLOR[n.jenis_setoran] || 'bg-gray-100 text-gray-600'}`}>
                              {n.jenis_setoran}
                            </span>
                            {!n.is_read && (
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm font-semibold text-gray-800 leading-snug">
                            Setoran baru dari {n.guru_nama}
                          </p>
                          {n.halaman_info && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {n.halaman_info}
                              {n.penilaian && (
                                <span className="ml-2 font-bold text-emerald-600">· Nilai: {n.penilaian}</span>
                              )}
                            </p>
                          )}
                          <p className="text-[10px] text-gray-400 mt-1">{formatRelative(n.created_at)}</p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* Bell button */}
      <button
        data-testid="notification-bell-btn"
        onClick={handleOpen}
        className="relative p-2 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-colors"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Portal panel */}
      {typeof document !== 'undefined' && createPortal(Panel, document.body)}
    </>
  );
}
